import { DEFAULT_COUNTRY_CONFIGS } from "@components/LocationPicker/locationCountryConfigs";

/**
 * Shared draft-wizard payload helpers.
 * UI form shape ↔ API request shape so backend wiring is a thin swap.
 *
 * ER-11729: `name` is gone — a pay range is scoped to a Job Family + Level and a
 * three-level Location (country-wide / region-wide / sub-region). The create-draft /
 * edit-draft body carries `job_family_level_id` plus a structured `location` object
 * (§8). The old `name` / `operating_country_id` / `operating_region` /
 * `is_country_wide` fields are no longer sent for the wizard.
 */

export const PAY_BASIS_TO_EXPRESSED_AS = {
  HOURLY: "per_hour",
  MONTHLY: "per_month",
  ANNUAL: "per_year",
};

export const EXPRESSED_AS_TO_PAY_BASIS = {
  per_hour: "HOURLY",
  per_month: "MONTHLY",
  per_year: "ANNUAL",
  HOURLY: "HOURLY",
  MONTHLY: "MONTHLY",
  ANNUAL: "ANNUAL",
};

/**
 * Canonical form payload emitted by PayRangeModal (create + edit).
 * @typedef {Object} PayRangeDraftFormPayload
 * @property {'create'|'edit'} mode
 * @property {string|null} payRangeId
 * @property {{jobFamilyId:*, jobFamilyName:string, code:string, levels:Array}|null} jobFamily
 * @property {*|null} jobFamilyLevelId - the Job Family Level id (§8 job_family_level_id)
 * @property {{countryCode:string, countryName?:string, scope:string, regionId?:*, regionName?:string,
 *   subRegionType?:string, subRegionId?:*, subRegionName?:string, operatingCountryId?:*}|null} location
 * @property {'ON_SITE'|'HYBRID'|'REMOTE'|'UNSPECIFIED'} workMode
 * @property {object|null} operatingCurrency
 * @property {'HOURLY'|'MONTHLY'|'ANNUAL'} payBasis
 * @property {number|null} minAmount
 * @property {number|null} maxAmount
 * @property {string|Array|null} sources
 * @property {string|Date|null} [effectiveFrom] - Effective from (YYYY-MM-DD)
 * @property {string|Date|null} [effectiveTo] - Effective to (YYYY-MM-DD); null = open-ended
 * @property {string|null} sourceDocumentId
 */

/**
 * Map LocationPicker's `scope` (country-wide / specific / region-wide / metro /
 * county) to the §8 scope_level enum: COUNTRY | REGION | SUB_REGION.
 */
export function scopeLevelFromLocation(location) {
  const scope = location?.scope;
  if (scope === "metro" || scope === "county") return "SUB_REGION";
  if (scope === "region-wide") return "REGION";
  // "specific" is only a final scope once a region id is attached; before that it is
  // an incomplete selection that still resolves country-wide.
  if (scope === "specific" && location?.regionId != null) return "REGION";
  return "COUNTRY";
}

/** Resolve a country display name from the location value / country config list. */
export function resolveCountryName(location, countries = []) {
  if (!location) return "";
  if (location.countryName) return location.countryName;
  const match = (countries || []).find((c) => c?.code === location.countryCode);
  return match?.name || location.countryCode || "";
}

/**
 * Build the header location path (e.g. ["United States", "Colorado", "Metro Denver"])
 * at whatever depth the location resolved. Only names that are actually known are
 * included, so there are never empty "•" separators (AC 11). Region / sub-region
 * names come from the resolved location value (edit round-trip or parent-enriched);
 * the country name falls back to the country config list.
 */
export function buildLocationPath(location, countries = []) {
  if (!location) return [];
  const parts = [resolveCountryName(location, countries)];
  if (location.regionName) parts.push(location.regionName);
  if (location.subRegionName) parts.push(location.subRegionName);
  return parts.filter((p) => String(p ?? "").trim() !== "");
}

/**
 * Whether Step 1 location satisfies required depth (country-wide vs region / sub-region).
 * @param {object|null|undefined} location — LocationPicker model
 * @param {object[]} [countryConfigs] — operating countries merged with known hierarchies
 */
export function isPayRangeLocationComplete(location, countryConfigs = []) {
  if (!location?.countryCode) return false;

  const code = String(location.countryCode).trim().toUpperCase();
  const config =
    (countryConfigs || []).find(
      (c) => String(c?.code || "").trim().toUpperCase() === code
    ) ||
    DEFAULT_COUNTRY_CONFIGS.find((c) => c.code === code) ||
    null;

  const scope = location.scope || "country-wide";
  if (scope === "country-wide") return true;

  const hasRegions = config?.hasRegions === true;
  const hasSubRegions = config?.hasSubRegions === true;
  if (!hasRegions && !hasSubRegions) return true;

  if (hasRegions && location.regionId == null) return false;

  const subScope =
    location.subRegionType ||
    (scope === "metro" || scope === "county" ? scope : null);
  if (subScope === "metro" || subScope === "county") {
    return location.subRegionId != null;
  }

  return true;
}

/**
 * Resolve account OperatingCountry PK for a LocationPicker value.
 * The picker often only stores `countryCode`; operating id may live on the
 * merged country row in `countryOptions` / `operatingItems` (ER-11872).
 */
export function resolveOperatingCountryId(
  location,
  countryOptions = [],
  operatingItems = []
) {
  if (!location?.countryCode) return null;
  if (location.operatingCountryId != null && location.operatingCountryId !== "") {
    return location.operatingCountryId;
  }

  const code = String(location.countryCode).trim().toUpperCase();
  const matchByCode = (list) =>
    (list || []).find(
      (c) => String(c?.code || "").trim().toUpperCase() === code
    );

  const fromOptions = matchByCode(countryOptions);
  if (fromOptions) {
    if (fromOptions.operatingCountryId != null) return fromOptions.operatingCountryId;
    // Catalog rows use `id` for catalog PK; enriched operating rows set `operatingCountryId`.
    if (fromOptions.catalogCountryId == null && fromOptions.id != null) {
      return fromOptions.id;
    }
  }

  const fromOperating = matchByCode(operatingItems);
  if (fromOperating) {
    if (fromOperating.operatingCountryId != null) return fromOperating.operatingCountryId;
    if (fromOperating.id != null) return fromOperating.id;
  }

  return null;
}

/**
 * Resolve account OperatingCurrency PK for a picker row (catalog or operating shape).
 */
export function resolveOperatingCurrencyId(currency, operatingItems = []) {
  if (!currency) return null;
  if (currency.operating_currency_id != null && currency.operating_currency_id !== "") {
    return currency.operating_currency_id;
  }

  const code = String(currency.code || currency.currency_code || "")
    .trim()
    .toUpperCase();
  const rawId = currency.id;

  const matchByCode = (list) =>
    (list || []).find(
      (row) =>
        String(row?.code || row?.currency_code || "")
          .trim()
          .toUpperCase() === code && code
    );

  const fromOperating = matchByCode(operatingItems);
  if (fromOperating) {
    return fromOperating.operating_currency_id ?? fromOperating.id ?? null;
  }

  if (rawId != null && rawId !== "") {
    const idMatch = (operatingItems || []).find(
      (row) => (row?.id ?? row?.operating_currency_id) === rawId
    );
    if (idMatch) return idMatch.operating_currency_id ?? idMatch.id ?? rawId;
  }

  return null;
}

/**
 * Map LocationPicker model → PayRangeCreateDraftSerializer / PayRangeEditDraftSerializer
 * geography fields (ER-11730).
 */
export function locationToDraftApiFields(location) {
  const empty = {
    location_scope: "country_wide",
    location_region_id: null,
    location_sub_region_type: null,
    location_sub_region_id: null,
  };
  if (!location) return empty;

  const scope = location.scope || "country-wide";
  if (scope === "country-wide") return empty;

  if (scope === "metro") {
    return {
      location_scope: "metro_area",
      location_region_id: location.regionId ?? null,
      location_sub_region_type: "metro_area",
      location_sub_region_id: location.subRegionId ?? null,
    };
  }
  if (scope === "county") {
    return {
      location_scope: "local_unit",
      location_region_id: location.regionId ?? null,
      location_sub_region_type: "local_unit",
      location_sub_region_id: location.subRegionId ?? null,
    };
  }
  if (scope === "region-wide" || (scope === "specific" && location.regionId != null)) {
    return {
      location_scope: "region_wide",
      location_region_id: location.regionId ?? null,
      location_sub_region_type: null,
      location_sub_region_id: null,
    };
  }
  return empty;
}

function normalizeSourcesForApi(sources) {
  if (sources == null || sources === "") return null;
  if (typeof sources === "string") return sources;
  if (Array.isArray(sources)) {
    try {
      return JSON.stringify(sources);
    } catch {
      return null;
    }
  }
  return String(sources);
}

/** Backend DateField needs YYYY-MM-DD; accepts Date, YYYY-MM-DD or ISO datetime strings. */
export function toApiDateString(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Maps wizard form → create-draft / edit-draft API body (ER-11730).
 * Effective dates are sent only when the form carries them (`null` clears Effective to).
 */
export function toDraftApiRequest(form) {
  const currencyId =
    resolveOperatingCurrencyId(
      form.operatingCurrency,
      form.operatingCurrencyItems || []
    ) ??
    form.operatingCurrency?.operating_currency_id ??
    form.operatingCurrency?.id ??
    null;
  const jobFamilyLevelId =
    form.jobFamilyLevelId ??
    form.jobFamily?.levelId ??
    form.jobFamilyLevel?.id ??
    null;
  const operatingCountryId = resolveOperatingCountryId(
    form.location,
    form.locationCountryOptions || [],
    form.operatingCountryItems || form.operatingCountries || []
  );

  return {
    job_family_level_id: jobFamilyLevelId,
    operating_country_id: operatingCountryId,
    operating_currency_id: currencyId,
    pay_basis: form.payBasis,
    work_mode: form.workMode || "UNSPECIFIED",
    min_amount: form.minAmount,
    max_amount: form.maxAmount,
    sources: normalizeSourcesForApi(form.sources),
    ...(form.effectiveFrom !== undefined ? { effective_from: toApiDateString(form.effectiveFrom) } : {}),
    ...(form.effectiveTo !== undefined ? { effective_to: toApiDateString(form.effectiveTo) } : {}),
    ...locationToDraftApiFields(form.location),
  };
}

/**
 * Wizard Step 1 → POST .../pay-range/suggest/ (ER-11735, aligned with ER-11730 scope).
 * Replaces legacy `range_name` + display-string country/region seed.
 * @param {PayRangeDraftFormPayload} form
 */
export function toSuggestApiRequest(form) {
  const location = form.location;
  const scopeLevel = scopeLevelFromLocation(location);
  const geo = locationToDraftApiFields(location);
  const operatingCountryId = resolveOperatingCountryId(
    location,
    form.locationCountryOptions || [],
    form.operatingCountryItems || form.operatingCountries || []
  );
  const currencyId =
    resolveOperatingCurrencyId(
      form.operatingCurrency,
      form.operatingCurrencyItems || []
    ) ??
    form.operatingCurrency?.operating_currency_id ??
    form.operatingCurrency?.id ??
    null;
  const jobFamilyLevelId =
    form.jobFamilyLevelId ??
    form.jobFamily?.levelId ??
    form.jobFamily?.level?.id ??
    null;

  const countryCode = String(location?.countryCode || "")
    .trim()
    .toUpperCase();
  const currencyCode = String(
    form.operatingCurrency?.code || form.operatingCurrency?.currency_code || ""
  )
    .trim()
    .toUpperCase();

  const pickerScope = String(location?.scope || "").toLowerCase();
  const isMetro = pickerScope === "metro";
  const isCounty = pickerScope === "county";
  const subRegionApiType = isCounty ? "local_unit" : isMetro ? "metro_area" : null;

  const body = {
    job_family_level_id: jobFamilyLevelId,
    scope_level: scopeLevel,
    operating_country_id: operatingCountryId,
    country_code: countryCode,
    operating_currency_id: currencyId,
    currency: currencyCode,
    work_mode: form.workMode || "UNSPECIFIED",
    pay_basis: form.payBasis || "ANNUAL",
    // Draft-style location keys (create-draft / suggest scope resolution).
    ...geo,
  };

  // Geography + draft aliases — dev suggest validates SUB_REGION like create-draft.
  if (location?.regionId != null) {
    body.location_region_id = location.regionId;
    body.region_id = location.regionId;
  }
  if (isMetro || isCounty) {
    body.scope_level = "SUB_REGION";
    body.location_scope = isCounty ? "local_unit" : "metro_area";
    body.location_sub_region_type = subRegionApiType;
    body.sub_region_type = subRegionApiType;
    // ER-11958 canonical suggest field (aliases coalesced server-side).
    body.sub_region_kind = subRegionApiType;
    if (location?.subRegionId != null) {
      body.location_sub_region_id = location.subRegionId;
      body.sub_region_id = location.subRegionId;
    }
  } else if (geo.location_sub_region_type) {
    body.sub_region_type = geo.location_sub_region_type;
    body.sub_region_kind = geo.location_sub_region_type;
    if (geo.location_sub_region_id != null) {
      body.sub_region_id = geo.location_sub_region_id;
    }
  } else if (geo.location_region_id != null) {
    body.region_id = geo.location_region_id;
  }

  return body;
}

/**
 * Map GET pay-range detail/list payload → PayRangeModal `initialValues`.
 */
export function payRangeApiToModalInitialValues(api = {}) {
  const level = api.job_family_level || null;
  const jf = level?.job_family || {};
  const jobFamily = level
    ? {
        jobFamilyId: jf.id,
        jobFamilyName: jf.name || "",
        code: jf.code || "",
        levelId: level.id,
        level: { id: level.id, ordinal: level.ordinal, name: level.name },
        levels: [{ id: level.id, ordinal: level.ordinal, name: level.name }],
      }
    : api.jobFamily || api.job_family || null;

  const oc = api.operating_country || {};
  const location = locationFromPayRangeApi(api);

  const payBasis =
    EXPRESSED_AS_TO_PAY_BASIS[api.pay_basis] ||
    EXPRESSED_AS_TO_PAY_BASIS[api.payBasis] ||
    "ANNUAL";

  const cur = api.operating_currency || {};
  const isDraft = String(api.status || "").toUpperCase() === "DRAFT";
  // ER-11975: for a first-ever DRAFT the staged compensation amounts live on
  // compensation_snapshot.min_amount / .max_amount, not on top-level
  // draft_min_amount / min_amount (those are reserved for a staged new version
  // on an already-PUBLISHED range). Mirror mapPayRangeDetailToCard.js.
  const snap = api.compensation_snapshot || {};

  return {
    jobFamily,
    jobFamilyLevelId: level?.id ?? api.job_family_level_id ?? jobFamily?.levelId ?? null,
    location,
    operatingCurrency: normalizeOperatingCurrencyForModal(
      {
        id: cur.id,
        operating_currency_id: cur.id,
        code: cur.code,
        name: cur.name,
        icon: oc.icon || oc.flag_icon,
      },
      { countryFlag: "" }
    ),
    workMode: api.work_mode || api.workMode || "UNSPECIFIED",
    payBasis,
    minAmount: isDraft
      ? snap.min_amount ?? api.draft_min_amount ?? api.min_amount
      : snap.min_amount ?? api.draft_min_amount,
    maxAmount: isDraft
      ? snap.max_amount ?? api.draft_max_amount ?? api.max_amount
      : snap.max_amount ?? api.draft_max_amount,
    sources: isDraft ? api.draft_sources ?? api.sources : api.sources,
    sourceDocument: api.draft_source_document || api.source_document || null,
    effectiveFrom: toApiDateString(api.draft_effective_from),
    effectiveTo: toApiDateString(api.draft_effective_to),
  };
}

/** LocationPicker model from a PayRange read serializer row. */
export function locationFromPayRangeApi(api = {}) {
  const oc = api.operating_country || {};
  const base = {
    countryCode: api.location_country_code || oc.code || "",
    operatingCountryId: oc.id ?? null,
    countryName: oc.name || "",
  };
  const scope = String(api.location_scope || "country_wide").toLowerCase();

  if (scope === "country_wide") {
    return { ...base, scope: "country-wide" };
  }

  const region = api.location_region;
  if (region) {
    base.regionId = region.id;
    base.regionName = region.display_label || region.name;
    base.regionCode = region.code;
  }

  if (scope === "region_wide") {
    return { ...base, scope: "region-wide" };
  }

  if (scope === "metro_area") {
    const metro = api.location_metro_area;
    return {
      ...base,
      scope: "metro",
      subRegionType: "metro",
      subRegionId: metro?.id ?? null,
      subRegionName: metro?.display_label || metro?.name || "",
    };
  }

  if (scope === "local_unit") {
    const unit = api.location_local_unit;
    return {
      ...base,
      scope: "county",
      subRegionType: "county",
      subRegionId: unit?.id ?? null,
      subRegionName: unit?.display_label || unit?.name || "",
    };
  }

  return { ...base, scope: "country-wide" };
}

/**
 * Detail/list location line from PayRange read serializer (location_scope + nested region/metro).
 * Falls back to legacy `operating_region` when scope fields are absent.
 * @param {object} api
 * @returns {string}
 */
export function formatPayRangeLocationLabel(api = {}) {
  const country = api.operating_country || {};
  const countryName =
    country.name || country.code || api.location_country_code || "Country";

  const legacyRegion = String(api.operating_region || "").trim();
  const scope = String(api.location_scope || "").toLowerCase();

  if (!scope && legacyRegion) {
    return `${countryName} - ${legacyRegion}`;
  }

  if (!scope || scope === "country_wide") {
    return `${countryName} - Country Wide`;
  }

  const location = locationFromPayRangeApi(api);
  const path = buildLocationPath(location);
  if (path.length > 1) {
    return path.join(" - ");
  }

  if (scope === "region_wide" && api.location_region) {
    const regionLabel =
      api.location_region.display_label || api.location_region.name || "";
    return regionLabel ? `${countryName} - ${regionLabel}` : `${countryName} - Country Wide`;
  }

  return `${countryName} - Country Wide`;
}

function normalizeSourceDocument(doc) {
  if (!doc || typeof doc !== "object") return null;
  return {
    name: doc.name || doc.original_filename || "",
    size: doc.size ?? 0,
    type: doc.type || "application/pdf",
  };
}

function flagcdnFromCode(code) {
  const c = String(code || "")
    .trim()
    .toLowerCase();
  if (!c || c.length !== 2) return "";
  return `https://flagcdn.com/${c}.svg`;
}

/**
 * Ensures currency picker selections include flag data for ExSelectPicker.
 * Always prefers a single `icon` (CountryPicker / Avatar path) so edit mode
 * shows a flag even when the API omits `countries`.
 * @param {object|null|undefined} currency
 * @param {{ countryFlag?: string }} [options]
 */
export function normalizeOperatingCurrencyForModal(currency, { countryFlag = "" } = {}) {
  if (!currency || typeof currency !== "object") return null;

  const name = currency.name || currency.currency_name || currency.code || "";
  const code = currency.code || currency.currency_code || "";
  const rawCountries = Array.isArray(currency.countries) ? currency.countries : [];
  const flags = Array.isArray(currency.flags)
    ? currency.flags.filter(Boolean)
    : [];
  const fromCountry = rawCountries.find((c) => c?.flag_icon || c?.icon);
  const icon =
    currency.icon ||
    currency.flag_icon ||
    fromCountry?.flag_icon ||
    fromCountry?.icon ||
    flags[0] ||
    flagcdnFromCode(currency.iso_country_code) ||
    flagcdnFromCode(rawCountries[0]?.code) ||
    countryFlag ||
    "";
  const { countries: _countries, ...currencyRest } = currency;

  return {
    ...currencyRest,
    id: currency.id ?? currency.operating_currency_id ?? null,
    name,
    code,
    ...(icon ? { icon } : {}),
  };
}

/**
 * Map a stored draft / list row → modal `initialValues`.
 * Accepts both the new §8 shape (job_family_level_id + location) and the legacy
 * shape (name / operating_country / operating_region / is_country_wide) so the
 * detail page keeps working until the backend (ER-11730) lands.
 */
export function toModalInitialValues(draft = {}) {
  const payBasis =
    EXPRESSED_AS_TO_PAY_BASIS[draft.payBasis] ||
    EXPRESSED_AS_TO_PAY_BASIS[draft.pay_basis] ||
    EXPRESSED_AS_TO_PAY_BASIS[draft.expressed_as] ||
    EXPRESSED_AS_TO_PAY_BASIS[draft.pay_range_header?.pay_range_expressed_as] ||
    "ANNUAL";

  const header = draft.pay_range_header || {};

  const jobFamily = draft.jobFamily || draft.job_family || null;
  const jobFamilyLevelId =
    draft.jobFamilyLevelId ??
    draft.job_family_level_id ??
    (jobFamily?.levelId ?? null);

  const location = draft.location || draft.scope || null;

  return {
    jobFamily,
    jobFamilyLevelId,
    location,
    operatingCurrency: normalizeOperatingCurrencyForModal(
      draft.operatingCurrency ||
        (header.pay_range_currency_code
          ? {
              id: draft.operating_currency_id || draft.currencyId || null,
              name: draft.currencyName || header.pay_range_currency_code,
              code: header.pay_range_currency_code,
            }
          : null),
      { countryFlag: header.country_flag || "" }
    ),
    workMode: draft.workMode || draft.work_mode || "UNSPECIFIED",
    payBasis,
    minAmount:
      draft.minAmount ??
      draft.bottom_of_range ??
      draft.pay_range_line?.zeroPenetration ??
      null,
    maxAmount:
      draft.maxAmount ??
      draft.top_of_range ??
      draft.pay_range_line?.fullPenetration ??
      null,
    sources: draft.sources ?? "",
    effectiveFrom: toApiDateString(draft.effectiveFrom ?? draft.draft_effective_from),
    effectiveTo: toApiDateString(draft.effectiveTo ?? draft.draft_effective_to),
    sourceDocumentId:
      draft.sourceDocumentId ??
      draft.source_document_id ??
      draft.sourceDocument?.id ??
      draft.source_document?.id ??
      null,
    sourceDocument: normalizeSourceDocument(
      draft.sourceDocument || draft.source_document || null
    ),
  };
}

/**
 * Builds a list-row shape from a saved draft form (local / optimistic UI).
 */
export function toListRowFromForm(form, { id, order = 1 } = {}) {
  const min = Number(form.minAmount) || 0;
  const max = Number(form.maxAmount) || 0;
  const span = Math.max(max - min, 0);
  const expressed =
    PAY_BASIS_TO_EXPRESSED_AS[form.payBasis] || "per_year";
  const headerName =
    form.jobFamily?.jobFamilyName ||
    form.jobFamilyName ||
    "";

  return {
    id,
    status: "draft",
    order,
    used: 0,
    name: headerName,
    jobFamily: form.jobFamily || null,
    jobFamilyLevelId: form.jobFamilyLevelId ?? null,
    location: form.location || null,
    workMode: form.workMode,
    payBasis: form.payBasis,
    sources: form.sources || "",
    sourceDocumentId: form.sourceDocumentId || null,
    sourceDocument: form.sourceDocument || null,
    operatingCurrency: form.operatingCurrency,
    minAmount: form.minAmount,
    maxAmount: form.maxAmount,
    pay_range_header: {
      country_flag: form.location?.countryFlag || form.operatingCountry?.icon || "",
      location: "",
      name: headerName,
      pay_range_expressed_as: expressed,
      pay_range_currency_code: form.operatingCurrency?.code || "",
    },
    pay_range_line: {
      expressed,
      zeroPenetration: min,
      oneThirdPenetration: min + span / 3,
      twoThirdPenetration: min + (span * 2) / 3,
      fullPenetration: max,
    },
  };
}
